import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    useGetAvailableFeatureFlagsQuery,
    useUpdateCompanyFeatureFlagsMutation,
} from '../../../services/featureFlag/featureFlag.service';
import { PermissionsUtil } from '../../../utils/permissions/PermissionsUtil';
import { PERMISSIONS } from '../../../constants/permissions/permissions.constants';
import { isNilOrEmpty } from '../../../utils/objectUtils';
import { FeatureFlag, Node } from '../../../types/Settings.types';
import { buildNodeFromPath } from '../../../components/TreeView/utils';
import * as _ from 'lodash';
import { RootState } from '../../../store';

const useFeatureFlags = () => {
    const user = useSelector((state: RootState) => state.user);
    const { data: availableFeatureFlags, isLoading } =
        useGetAvailableFeatureFlagsQuery();
    const [updateCompanyFeatureFlags] = useUpdateCompanyFeatureFlagsMutation();
    const [treeData, setTreeData] = useState([]);
    const [updatedFeatures, setUpdatedFeatures] = useState([]);
    const [isUnchanged, setIsUnchanged] = useState(true);
    const [dataSaved, setDataSaved] = useState(false);
    const toggleDataSaved = () => setDataSaved(!dataSaved);
    const canEditFeatureFlags = PermissionsUtil.isPermissionEnabled(
        user.permissions,
        PERMISSIONS.SETTINGS.FEATURE_FLAGS.EDIT
    );

    useEffect(() => {
        if (!isLoading && !isNilOrEmpty(availableFeatureFlags)) {
            buildNodes();
        }
    }, [isLoading]);

    const buildNodes = () => {
        const newTreeNodes: Array<any> = [];
        setUpdatedFeatures([]);

        availableFeatureFlags.forEach((featureFlag: FeatureFlag) => {
            const flagNode: Node = buildNodeFromPath(
                newTreeNodes,
                featureFlag.path
            );
            const isEnabled = user.enabledFeatureFlags.includes(
                featureFlag.flagKey
            );

            flagNode.children.push({
                label: featureFlag.description,
                value: featureFlag.flagKey,
                disabled: !canEditFeatureFlags,
                checked: isEnabled,
                children: [],
            });
        });

        setTreeData(_.orderBy(newTreeNodes, ['label'], ['asc']));
    };

    const onCheckChange = (node: Node) => {
        if (_.some(updatedFeatures, (flag) => flag.flagKey === node.value)) {
            _.remove(updatedFeatures, (flag) => flag.flagKey === node.value);
        } else {
            updatedFeatures.push({
                flagKey: node.value,
                enabled: node.checked,
            });
        }

        setIsUnchanged(isNilOrEmpty(updatedFeatures));
    };

    const updateFeatureFlags = async () => {
        updateCompanyFeatureFlags(updatedFeatures);
        setDataSaved(true);

        setUpdatedFeatures([]);
        setIsUnchanged(true);
    };

    return {
        isLoading,
        treeData,
        onCheckChange,
        dataSaved,
        toggleDataSaved,
        canEditFeatureFlags,
        updateFeatureFlags,
        isUnchanged,
    };
};

export default useFeatureFlags;
